import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { ExportFileType, SearchParam } from '@common/ts/enums';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import {
  useMutateDownloadConversationDetails,
  useMutateDownloadConversationOverview,
  useMutateDownloadConversation,
  useMutateDownloadFullAnswersTranscript,
} from '@/reactQuery/get';
import { ExportModalProps, ExportOption, MultipleConversationsView } from '@/types';
import { ConversationView } from '@/enums';
import exportConversation from '@/utils/exportConversation';
import ExportFileTypeToggle from '@/components/Modals/ExportModal/ExportFileTypeToggle';
import ExportOptionsDropdown from '@/components/Modals/ExportModal/ExportOptionsDropdown';

export default function ExportModal({
  isOpen,
  onClose,
  projectId,
  transcriptsView,
}: ExportModalProps) {
  const { lang } = useTranslation();
  const exportOptions: ExportOption[] = [
    {
      label: lang.get('msg.exportConversationOverview'),
      value: ConversationView.CONVERSATION_OVERVIEW,
    },
    {
      label: lang.get('msg.exportConversationDetails'),
      value: ConversationView.CONVERSATION_DETAILS,
    },
    {
      label: lang.get('msg.exportFullAnswersTranscript'),
      value: ConversationView.FULL_ANSWERS_TRANSCRIPT,
    },
  ];
  const [selectedOption, setSelectedOption] = useState<ExportOption | null>(null);
  const [fileType, setFileType] = useState<ExportFileType>(ExportFileType.CSV);
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const conversationId = searchParams.get(SearchParam.SELECTED_TRANSCRIPT);
  const isSingleConversationScreen = !!conversationId;

  const {
    mutateAsync: mutateAsyncDownloadConversationDetails,
    isPending: isDownloadingConversationDetailsPending,
  } = useMutateDownloadConversationDetails();

  const {
    mutateAsync: mutateAsyncDownloadConversationOverview,
    isPending: isDownloadingConversationOverviewPending,
  } = useMutateDownloadConversationOverview();

  const {
    mutateAsync: mutateAsyncDownloadConversation,
    isPending: isDownloadingConversationPending,
  } = useMutateDownloadConversation(projectId);

  const {
    mutateAsync: mutateAsyncDownloadFullAnswersTranscript,
    isPending: isDownloadingFullAnswersTranscriptPending,
  } = useMutateDownloadFullAnswersTranscript();

  // Helper function to execute export with the right parameters
  const executeExport = async (type: ConversationView) => {
    await exportConversation({
      type,
      projectId,
      conversationId,
      filterId,
      fileType,
      transcriptsView,
      mutations: {
        overview: mutateAsyncDownloadConversationOverview,
        details: mutateAsyncDownloadConversationDetails,
        single: mutateAsyncDownloadConversation,
        fullAnswersTranscript: mutateAsyncDownloadFullAnswersTranscript,
      },
      translations: {
        overview: lang.get('msg.conversationOverviewSuccessfullyDownloaded'),
        details: lang.get('msg.allConversationDetailsSuccessfullyDownloaded'),
        single: lang.get('msg.conversationSuccessfullyDownloaded'),
        fullAnswersTranscript: lang.get('msg.fullAnswersTranscriptSuccessfullyDownloaded'),
        error: lang.get('msg.errorPleaseTryAgain'),
      },
    });
  };

  const exportConversationOverview = () => executeExport(ConversationView.CONVERSATION_OVERVIEW);
  const exportConversationDetails = () => executeExport(ConversationView.CONVERSATION_DETAILS);
  const exportSingleConversation = () => executeExport(ConversationView.SINGLE_CONVERSATION);
  const exportFullAnswersTranscript = () => executeExport(ConversationView.FULL_ANSWERS_TRANSCRIPT);

  const onCloseHandler = () => {
    onClose();
    setSelectedOption(null);
    setFileType(ExportFileType.CSV);
  };

  // Handle export button click
  const handleExport = async () => {
    try {
      // If we're on the single transcript page, use that export function directly
      if (isSingleConversationScreen) {
        await exportSingleConversation();
      } else {
        // Otherwise, use the selected option for regular exports
        if (!selectedOption) return;

        const actions: Record<MultipleConversationsView, () => Promise<void>> = {
          [ConversationView.CONVERSATION_OVERVIEW]: exportConversationOverview,
          [ConversationView.CONVERSATION_DETAILS]: exportConversationDetails,
          [ConversationView.FULL_ANSWERS_TRANSCRIPT]: exportFullAnswersTranscript,
        };

        await actions[selectedOption.value as MultipleConversationsView]();
      }

      onCloseHandler();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const isExporting =
    isDownloadingConversationDetailsPending ||
    isDownloadingConversationOverviewPending ||
    isDownloadingConversationPending ||
    isDownloadingFullAnswersTranscriptPending;

  const isExportDisabled = isExporting || (!isSingleConversationScreen && selectedOption === null);

  return (
    <Dialog open={isOpen} onOpenChange={onCloseHandler}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{lang.get('msg.exportData')}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 items-center">
          {!isSingleConversationScreen ? (
            <>
              <ExportOptionsDropdown
                exportOptions={exportOptions}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
              />

              <span className="text-sm mx-1">{lang.get('msg.as')}</span>
            </>
          ) : (
            <p className="text-sm">{lang.get('msg.exportConversation')}</p>
          )}

          <ExportFileTypeToggle fileType={fileType} setFileType={setFileType} />
        </div>

        <DialogFooter className="flex justify-end mt-11">
          <Button variant="outline" onClick={onCloseHandler} disabled={isExporting}>
            {lang.get('msg.cancel')}
          </Button>
          <Button onClick={handleExport} disabled={isExportDisabled}>
            {isExporting && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />}
            {lang.get(isExporting ? 'msg.exporting' : 'msg.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
